import { useEffect, useRef } from 'react'
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react'
import { isHtmlEmpty, sanitizeHtml } from '../lib/html-utils.js'

/**
 * Minimal contenteditable rich-text editor.
 *   - Bold / Italic / Underline / Bullet list / Numbered list
 *   - Value is sanitized HTML (allowlist: b, strong, i, em, u, ul, ol, li, br, p, div, span)
 *   - No external dependencies (uses document.execCommand for portability)
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 80 }) {
  const ref         = useRef(null)
  const lastSet     = useRef('')
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange })

  // Sync DOM only when the external value changes; never on every keystroke
  // (that would reset the cursor while typing).
  useEffect(() => {
    if (!ref.current) return
    const next = value || ''
    if (next !== lastSet.current) {
      ref.current.innerHTML = next
      lastSet.current = next
    }
  }, [value])

  function emit() {
    if (!ref.current) return
    const raw   = ref.current.innerHTML
    const clean = sanitizeHtml(raw)
    lastSet.current = clean
    onChangeRef.current?.(clean)
  }

  function exec(cmd) {
    document.execCommand(cmd, false)
    ref.current?.focus()
    emit()
  }

  // Live markdown shortcuts: convert **bold**, *italic*, __bold__, _italic_
  // on space; convert "- " / "1. " to bullet/numbered list at line start.
  // We use the native `beforeinput` event (attached via useEffect) because
  // React's synthetic onBeforeInput doesn't reliably forward it for contentEditable.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    function onBeforeInput(e) {
      if (e.inputType !== 'insertText' || e.data !== ' ') return
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      const range = sel.getRangeAt(0)
      if (!range.collapsed) return
      const node = range.startContainer
      if (!node || node.nodeType !== Node.TEXT_NODE) return
      const offset = range.startOffset
      const before = node.textContent.slice(0, offset)

      // List shortcuts at start of (logical) line.
      const lineMatch = before.match(/(?:^|\n)([-*]|\d+\.)$/)
      if (lineMatch) {
        const marker = lineMatch[1]
        e.preventDefault()
        selectRange(sel, node, offset - marker.length, offset)
        document.execCommand('delete')
        document.execCommand(/^\d/.test(marker) ? 'insertOrderedList' : 'insertUnorderedList')
        emit()
        return
      }

      // Bold: **text** or __text__
      const bold = before.match(/(\*\*|__)([^*_\n]+)\1$/)
      if (bold) {
        e.preventDefault()
        selectRange(sel, node, offset - bold[0].length, offset)
        document.execCommand('delete')
        document.execCommand('insertHTML', false, `<b>${escapeHtml(bold[2])}</b>&nbsp;`)
        emit()
        return
      }

      // Italic: single *text* or _text_ (not part of bold)
      const italic = before.match(/(?:^|[^*_])([*_])([^*_\n]+)\1$/)
      if (italic) {
        e.preventDefault()
        const totalLen = italic[1].length + italic[2].length + italic[1].length
        selectRange(sel, node, offset - totalLen, offset)
        document.execCommand('delete')
        document.execCommand('insertHTML', false, `<i>${escapeHtml(italic[2])}</i>&nbsp;`)
        emit()
        return
      }
    }
    el.addEventListener('beforeinput', onBeforeInput)
    return () => el.removeEventListener('beforeinput', onBeforeInput)
  }, [])

  const empty = isHtmlEmpty(value)

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-slate-100">
        <ToolBtn onClick={() => exec('bold')}                  icon={<Bold size={13} strokeWidth={2.25} />}        label="Bold (Ctrl+B)" />
        <ToolBtn onClick={() => exec('italic')}                icon={<Italic size={13} strokeWidth={2.25} />}      label="Italic (Ctrl+I)" />
        <ToolBtn onClick={() => exec('underline')}             icon={<Underline size={13} strokeWidth={2.25} />}   label="Underline (Ctrl+U)" />
        <span className="w-px h-4 bg-slate-200 mx-1" />
        <ToolBtn onClick={() => exec('insertUnorderedList')}   icon={<List size={13} strokeWidth={2.25} />}        label="Bullet list" />
        <ToolBtn onClick={() => exec('insertOrderedList')}     icon={<ListOrdered size={13} strokeWidth={2.25} />} label="Numbered list" />
      </div>
      <div className="relative">
        {empty && placeholder && (
          <div className="absolute top-2 left-2.5 text-slate-400 pointer-events-none text-[14.5px] select-none">
            {placeholder}
          </div>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          className="px-2.5 py-2 text-[14.5px] outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500/40 focus:bg-brand-50/20 transition-colors [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold"
          style={{ minHeight }}
        />
      </div>
    </div>
  )
}

function selectRange(sel, node, start, end) {
  const r = document.createRange()
  r.setStart(node, start)
  r.setEnd(node, end)
  sel.removeAllRanges()
  sel.addRange(r)
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function ToolBtn({ onClick, icon, label }) {
  return (
    <button
      type="button"
      // onMouseDown + preventDefault avoids stealing focus from the editor
      // before the command runs (otherwise the selection is lost on click).
      onMouseDown={(e) => { e.preventDefault(); onClick() }}
      title={label}
      className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900"
    >
      {icon}
    </button>
  )
}
